// src/pages/FlaggedRecapturesPage.js
// Admin only. Captures of FLAGGED service numbers are held here until the
// admin approves (slip is generated) or rejects (person stays not captured).
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Paper, Typography, Box, Button, TextField, Alert, CircularProgress, Grid,
  IconButton, Chip, Stack, Table, TableBody, TableRow, TableCell, TableHead, TableContainer,
  Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel,
  Divider, Tooltip,
} from '@mui/material';
import {
  ArrowBack as BackIcon, Refresh as RefreshIcon, Search as SearchIcon,
  CheckCircle as ApproveIcon, Cancel as RejectIcon, Close as CloseIcon,
} from '@mui/icons-material';
import {
  getFlaggedList, getFlaggedPending, getFlagDetails, runFlaggedFaceCheck,
  approveFlaggedRecapture, rejectFlaggedRecapture, getFaceCheckHealth,
} from '../services/verificationService';

const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace('/api', '');
const fmt = (d) => (d ? new Date(d).toLocaleString('en-GB') : '—');
const STRONG = 0.6;
const POSSIBLE = 0.5;

const scoreChip = (score) => {
  if (score === null || score === undefined) return <Chip size="small" label="—" />;
  const color = score >= STRONG ? 'error' : score >= POSSIBLE ? 'warning' : 'default';
  return <Chip size="small" color={color} label={score.toFixed(2)} />;
};

const originalText = (o) => {
  if (!o) return 'Reversed slip not available for comparison';
  if (o.score === null) return 'No face found on the reversed slip';
  if (o.score >= STRONG) return 'Same face as the reversed capture';
  if (o.score >= POSSIBLE) return 'Possibly the same face as the reversed capture';
  return 'Different face from the reversed capture';
};

const warningsOf = (fc) => {
  const w = [];
  if (!fc || fc.ok === false) w.push('The face check did not run.');
  else {
    if (fc.newFace === false) w.push('No face was found in the new photo.');
    if ((fc.strongOtherMatches || 0) > 0) w.push('The new photo strongly matches the portrait on another slip.');
  }
  return w;
};

const Photo = ({ src, label, fallbackLink }) => {
  const [failed, setFailed] = useState(false);
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="subtitle2" gutterBottom>{label}</Typography>
      {src && !failed ? (
        <Box component="img" src={src} alt={label} onError={() => setFailed(true)}
          sx={{ width: '100%', maxHeight: 340, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 1, bgcolor: '#fafafa' }} />
      ) : (
        <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed #bbb', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">Photo not available</Typography>
        </Box>
      )}
      {fallbackLink && (
        <Button size="small" href={fallbackLink} target="_blank" rel="noreferrer" sx={{ mt: 1 }}>Open reversed slip</Button>
      )}
    </Box>
  );
};

const ReviewDialog = ({ pendingId, onClose, onDone }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [override, setOverride] = useState(false);
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await getFlaggedPending(pendingId);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load');
    }
  }, [pendingId]);

  useEffect(() => { load(); }, [load]);

  // the face check runs in the background after capture; refresh until it is there
  useEffect(() => {
    if (!data || data.pending.faceCheck || data.pending.status !== 'PENDING') return undefined;
    const t = setTimeout(load, 5000);
    return () => clearTimeout(t);
  }, [data, load]);

  const rerun = async () => {
    setBusy(true); setError('');
    try { await runFlaggedFaceCheck(pendingId); await load(); }
    catch (e) { setError(e.response?.data?.message || 'Face check failed'); }
    finally { setBusy(false); }
  };

  const act = async (kind) => {
    setBusy(true); setError(''); setMessage('');
    try {
      const r = kind === 'approve'
        ? await approveFlaggedRecapture(pendingId, { note, overrideWarning: override })
        : await rejectFlaggedRecapture(pendingId, { note });
      setMessage(r.message + (r.data?.referenceNumber ? ` Reference: ${r.data.referenceNumber}` : ''));
      await load();
      onDone();
    } catch (e) {
      setError(e.response?.data?.message || 'Action failed');
    } finally { setBusy(false); }
  };

  const p = data?.pending;
  const flag = data?.flag;
  const person = data?.personnel;
  const fc = p?.faceCheck;
  const warnings = p ? warningsOf(fc) : [];
  const decided = p && p.status !== 'PENDING';

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ flex: 1 }}>
          Flagged recapture {p ? `— ${p.serviceNumber} ${p.fullName || ''}` : ''}
        </Box>
        {p && <Chip label={p.status} color={p.status === 'APPROVED' ? 'success' : p.status === 'REJECTED' ? 'error' : 'warning'} sx={{ mr: 1 }} />}
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {!data && !error && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
        {data && (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Photo src={p.photoBase64} label={`New capture — ${fmt(p.submittedAt)}`} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Photo
                  src={flag?.originalPhotoPath ? `${apiBase}/pdfs/${flag.originalPhotoPath}` : null}
                  label={`Reversed capture — ${fmt(flag?.originalVerifiedAt)}`}
                  fallbackLink={flag?.originalSlipPath ? `${apiBase}/pdfs/${flag.originalSlipPath}` : null}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" gutterBottom>Details</Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow><TableCell>Service No.</TableCell><TableCell>{p.serviceNumber}</TableCell></TableRow>
                    <TableRow><TableCell>Name</TableCell><TableCell>{p.fullName}</TableCell></TableRow>
                    <TableRow><TableCell>Rank</TableCell><TableCell>{person?.rankName}</TableCell></TableRow>
                    <TableRow><TableCell>Command</TableCell><TableCell>{person?.command?.name}</TableCell></TableRow>
                    <TableRow><TableCell>Captured by</TableCell><TableCell>{p.submittedByName}</TableCell></TableRow>
                    <TableRow><TableCell>Pre-cleared by</TableCell><TableCell>
                      {person?.preClearance?.clearingOfficerPersonnel
                        ? `${person.preClearance.clearingOfficerPersonnel.serviceNumber} ${person.preClearance.clearingOfficerPersonnel.lastName} ${person.preClearance.clearingOfficerPersonnel.firstName}`
                        : (person?.preClearance?.notes || '—')}
                    </TableCell></TableRow>
                    <TableRow><TableCell>Phone / Email</TableCell><TableCell>{p.phoneNumber || '—'} / {p.email || '—'}</TableCell></TableRow>
                    <TableRow><TableCell>Complaints</TableCell><TableCell>{(p.complaints || []).length || 'None'}</TableCell></TableRow>
                    <TableRow><TableCell>Reason flagged</TableCell><TableCell>{flag?.finding}</TableCell></TableRow>
                    <TableRow><TableCell>Reversed capture</TableCell><TableCell>{flag?.originalReference}<br />by {flag?.originalVerifiedByName}</TableCell></TableRow>
                  </TableBody>
                </Table>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ flex: 1 }}>Face check</Typography>
              {!decided && (
                <Button size="small" startIcon={<RefreshIcon />} onClick={rerun} disabled={busy}>Run again</Button>
              )}
            </Box>
            {!fc && <Alert severity="info">Face check is running. This refreshes automatically.</Alert>}
            {fc && fc.ok === false && <Alert severity="warning">{fc.error}</Alert>}
            {fc && fc.ok !== false && (
              <>
                {fc.newFace === false ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>No face was found in the new photo.</Alert>
                ) : (
                  <Alert severity={fc.original?.score >= STRONG ? 'info' : 'success'} sx={{ mb: 2 }}>
                    <strong>Compared with the reversed capture:</strong> {scoreChip(fc.original?.score)} {originalText(fc.original)}
                    {fc.facesInPhoto > 1 && <> — note: {fc.facesInPhoto} faces in the new photo, the largest was used.</>}
                  </Alert>
                )}
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Closest portraits on other slips ({fc.indexed} of {fc.totalSlips} slips checked{fc.indexBuilding ? ', index still building' : ''}).
                  A score of {STRONG.toFixed(2)} or more is very likely the same face; {POSSIBLE.toFixed(2)}–{STRONG.toFixed(2)} is possible. Twins and close relatives can score high.
                </Typography>
                {(fc.matches || []).length === 0 ? (
                  <Alert severity="success">No close match on any other slip.</Alert>
                ) : (
                  <Grid container spacing={2}>
                    {fc.matches.map((m) => (
                      <Grid item xs={6} sm={4} md={3} key={m.file}>
                        <Paper variant="outlined" sx={{ p: 1, borderColor: !m.samePerson && m.score >= STRONG ? 'error.main' : undefined }}>
                          {m.thumb && <Box component="img" src={m.thumb} alt="" sx={{ width: '100%', height: 160, objectFit: 'contain' }} />}
                          <Box sx={{ mt: 0.5 }}>{scoreChip(m.score)} {m.kind === 'REVERSED_SLIP' && <Chip size="small" label="reversed slip" sx={{ ml: 0.5 }} />}</Box>
                          <Typography variant="caption" display="block"><strong>{m.serviceNumber || '?'}</strong> {m.name}</Typography>
                          <Typography variant="caption" display="block">{m.command}</Typography>
                          <Typography variant="caption" display="block">by {m.capturedBy || '?'} · {fmt(m.capturedAt)}</Typography>
                          {m.samePerson && <Typography variant="caption" color="success.main" display="block">Same service number</Typography>}
                          <Button size="small" href={`${apiBase}/pdfs/${m.file}`} target="_blank" rel="noreferrer">Slip</Button>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>Checked {fmt(fc.checkedAt)}</Typography>
              </>
            )}

            {data.history.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>Earlier attempts</Typography>
                <Table size="small">
                  <TableBody>
                    {data.history.map((h) => (
                      <TableRow key={h.id}>
                        <TableCell>{h.status}</TableCell>
                        <TableCell>{fmt(h.submittedAt)} by {h.submittedByName}</TableCell>
                        <TableCell>{h.reviewedByName ? `${h.reviewedByName}, ${fmt(h.reviewedAt)}` : ''}</TableCell>
                        <TableCell>{h.reviewNote}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}

            {decided ? (
              <Alert severity="info" sx={{ mt: 3 }}>
                {p.status} by {p.reviewedByName} on {fmt(p.reviewedAt)}{p.reviewNote ? ` — ${p.reviewNote}` : ''}
                {p.referenceNumber ? ` — Reference ${p.referenceNumber}` : ''}
              </Alert>
            ) : (
              <>
                <Divider sx={{ my: 3 }} />
                {warnings.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {warnings.map((w) => <div key={w}>{w}</div>)}
                    <FormControlLabel sx={{ mt: 1 }}
                      control={<Checkbox checked={override} onChange={(e) => setOverride(e.target.checked)} />}
                      label="I have looked at this and approve anyway (a note is required)" />
                  </Alert>
                )}
                <TextField fullWidth multiline minRows={2} label="Note (required to reject, or to approve with a warning)"
                  value={note} onChange={(e) => setNote(e.target.value)} />
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {p && !decided && (
          <>
            <Button color="error" variant="outlined" startIcon={<RejectIcon />} disabled={busy || !note.trim()}
              onClick={() => act('reject')}>Reject</Button>
            <Button color="success" variant="contained" startIcon={<ApproveIcon />}
              disabled={busy || !fc || (warnings.length > 0 && (!override || !note.trim()))}
              onClick={() => act('approve')}>Approve and issue slip</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

const FlagDialog = ({ flagId, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    getFlagDetails(flagId).then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.message || 'Failed to load'));
  }, [flagId]);
  const f = data?.flag;
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Flag {f ? `— ${f.serviceNumber} ${f.fullName || ''}` : ''}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error">{error}</Alert>}
        {!data && !error && <CircularProgress />}
        {f && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Photo src={f.originalPhotoPath ? `${apiBase}/pdfs/${f.originalPhotoPath}` : null}
                label="Reversed capture" fallbackLink={f.originalSlipPath ? `${apiBase}/pdfs/${f.originalSlipPath}` : null} />
            </Grid>
            <Grid item xs={12} md={7}>
              <Table size="small"><TableBody>
                <TableRow><TableCell>Status</TableCell><TableCell>{f.status}</TableCell></TableRow>
                <TableRow><TableCell>Rank / Command</TableCell><TableCell>{f.rankName} — {f.commandName}</TableCell></TableRow>
                <TableRow><TableCell>Reason flagged</TableCell><TableCell>{f.finding}</TableCell></TableRow>
                <TableRow><TableCell>Reversed capture</TableCell><TableCell>{f.originalReference}, {fmt(f.originalVerifiedAt)}<br />by {f.originalVerifiedByName}</TableCell></TableRow>
                <TableRow><TableCell>Pre-cleared by</TableCell><TableCell>{f.originalClearingOfficer || '—'}</TableCell></TableRow>
                <TableRow><TableCell>Saved complaints</TableCell><TableCell>{(f.savedComplaints || []).map((c, i) => <div key={i}>{c.type}: {c.details}</div>)}</TableCell></TableRow>
                <TableRow><TableCell>Flagged</TableCell><TableCell>{fmt(f.flaggedAt)} by {f.flaggedByName}</TableCell></TableRow>
                {f.status === 'CLEARED' && (
                  <TableRow><TableCell>Cleared</TableCell><TableCell>{fmt(f.clearedAt)} by {f.clearedByName} — {f.clearedReference}{f.clearedNote ? ` — ${f.clearedNote}` : ''}</TableCell></TableRow>
                )}
              </TableBody></Table>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Recapture attempts</Typography>
              {data.attempts.length === 0 ? <Typography variant="body2">None yet</Typography> : (
                <Table size="small"><TableBody>
                  {data.attempts.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>{a.status}</TableCell>
                      <TableCell>{fmt(a.submittedAt)} by {a.submittedByName}</TableCell>
                      <TableCell>{a.reviewNote}</TableCell>
                    </TableRow>
                  ))}
                </TableBody></Table>
              )}
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
};

const VIEWS = ['pending', 'active', 'cleared', 'all'];

const FlaggedRecapturesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [view, setView] = useState('pending');
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openPending, setOpenPending] = useState(new URLSearchParams(location.search).get('open'));
  const [openFlag, setOpenFlag] = useState(null);
  const [health, setHealth] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await getFlaggedList(view, q);
      setItems(r.data.items || []);
      if (r.data.summary) setSummary(r.data.summary);
      if (view === 'pending') {
        const s = await getFlaggedList('active', '');
        setSummary(s.data.summary);
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load');
    } finally { setLoading(false); }
  }, [view, q]);

  useEffect(() => { load(); }, [view]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    getFaceCheckHealth().then((r) => setHealth(r.data)).catch(() => setHealth({ ok: false }));
  }, []);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2.5}>
          <IconButton onClick={() => navigate('/')}><BackIcon /></IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="600"
              sx={{ color: '#00a651', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '1.1rem' }}>
              Flagged Recaptures
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Captures of flagged service numbers wait here for admin approval. Everyone else is captured as normal.
            </Typography>
          </Box>
          {health && (
            <Tooltip title={health.ok ? `Last scan ${health.lastScan || '—'}` : (health.error || 'Not reachable')}>
              <Chip size="small" color={health.ok ? (health.indexBuilding ? 'warning' : 'success') : 'error'}
                label={health.ok
                  ? `Face check: ${health.indexed}/${health.totalSlips} slips${health.indexBuilding ? ' (building)' : ''}`
                  : 'Face check offline'} />
            </Tooltip>
          )}
        </Box>
      </Paper>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={VIEWS.indexOf(view)} onChange={(_, i) => setView(VIEWS[i])} variant="scrollable">
          <Tab label={`Awaiting approval${summary ? ` (${summary.pending})` : ''}`} />
          <Tab label={`Flagged, not yet cleared${summary ? ` (${summary.active})` : ''}`} />
          <Tab label={`Cleared${summary ? ` (${summary.cleared})` : ''}`} />
          <Tab label="All" />
        </Tabs>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField size="small" label="Service number or name" value={q}
            onChange={(e) => setQ(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && load()} sx={{ minWidth: 280 }} />
          <Button variant="outlined" startIcon={<SearchIcon />} onClick={load}>Search</Button>
          <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
        </Stack>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : (
          <TableContainer>
            {view === 'pending' ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Service No.</TableCell><TableCell>Name</TableCell><TableCell>Command</TableCell>
                    <TableCell>Captured by</TableCell><TableCell>Captured</TableCell>
                    <TableCell>Vs reversed photo</TableCell><TableCell>Other slips</TableCell><TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 && <TableRow><TableCell colSpan={8}>Nothing awaiting approval.</TableCell></TableRow>}
                  {items.map((r) => {
                    const s = r.faceSummary;
                    return (
                      <TableRow key={r.id} hover>
                        <TableCell>{r.serviceNumber}</TableCell>
                        <TableCell>{r.fullName}</TableCell>
                        <TableCell>{r.commandName}</TableCell>
                        <TableCell>{r.submittedByName}</TableCell>
                        <TableCell>{fmt(r.submittedAt)}</TableCell>
                        <TableCell>{!s ? <Chip size="small" label="checking…" /> : s.ok === false ? <Chip size="small" color="warning" label="not run" /> : s.newFace === false ? <Chip size="small" color="warning" label="no face" /> : scoreChip(s.originalScore)}</TableCell>
                        <TableCell>{s && s.ok !== false && (s.strongOtherMatches > 0
                          ? <Chip size="small" color="error" label={`${s.strongOtherMatches} strong match`} />
                          : <Chip size="small" label={s.topScore ? `top ${s.topScore.toFixed(2)}` : 'none'} />)}</TableCell>
                        <TableCell><Button size="small" variant="contained" onClick={() => setOpenPending(r.id)}>Review</Button></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Service No.</TableCell><TableCell>Name</TableCell><TableCell>Command</TableCell>
                    <TableCell>Reason flagged</TableCell><TableCell>Reversed capture by</TableCell>
                    <TableCell>Status</TableCell><TableCell>Attempts</TableCell><TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 && <TableRow><TableCell colSpan={8}>No records.</TableCell></TableRow>}
                  {items.map((f) => (
                    <TableRow key={f.id} hover>
                      <TableCell>{f.serviceNumber}</TableCell>
                      <TableCell>{f.fullName}</TableCell>
                      <TableCell>{f.commandName}</TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>{f.finding}</TableCell>
                      <TableCell>{f.originalVerifiedByName}</TableCell>
                      <TableCell>
                        <Chip size="small" color={f.status === 'CLEARED' ? 'success' : 'warning'} label={f.status} />
                        {f.status === 'CLEARED' && <Typography variant="caption" display="block">{f.clearedReference}</Typography>}
                      </TableCell>
                      <TableCell>{f.pendingCount ? `${f.pendingCount} awaiting` : ''}{f.rejectedCount ? ` ${f.rejectedCount} rejected` : ''}</TableCell>
                      <TableCell><Button size="small" onClick={() => setOpenFlag(f.id)}>Details</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        )}
      </Paper>

      {openPending && (
        <ReviewDialog pendingId={openPending}
          onClose={() => { setOpenPending(null); navigate('/flagged-recaptures', { replace: true }); load(); }}
          onDone={load} />
      )}
      {openFlag && <FlagDialog flagId={openFlag} onClose={() => setOpenFlag(null)} />}
    </Container>
  );
};

export default FlaggedRecapturesPage;
